import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase.ts';
import {
  formatMoney,
  formatSlotRange,
  ORDER_STATUS_LABEL,
  FULFILLMENT_LABEL,
  PAYMENT_LABEL,
  phoneHref,
} from '../lib/format.ts';
import type { OrderView, OrderStatus } from '../lib/types.ts';

async function fetchOrder(orderId: string, token: string): Promise<OrderView> {
  const { data, error } = await supabase.rpc('get_order_by_token', {
    p_order_id: orderId,
    p_token: token,
  });
  if (error) throw error;
  return data as OrderView;
}

const TERMINAL_STATUSES: OrderStatus[] = ['completed', 'cancelled', 'rejected'];

const STATUS_ICON: Record<OrderStatus, string> = {
  new: '🕐',
  accepted: '✅',
  cooking: '🔥',
  ready: '🎉',
  completed: '✓',
  cancelled: '✕',
  rejected: '✕',
};

const STATUS_COLOR: Record<OrderStatus, string> = {
  new: 'var(--info)',
  accepted: 'var(--success)',
  cooking: 'var(--warning)',
  ready: 'var(--success)',
  completed: 'var(--text-muted)',
  cancelled: 'var(--danger)',
  rejected: 'var(--danger)',
};

export function OrderStatusPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const token = searchParams.get('token') ?? '';
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const { data: order, isLoading, isError } = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => fetchOrder(orderId!, token),
    enabled: !!orderId && !!token,
    staleTime: 30_000,
  });

  // Realtime subscription
  useEffect(() => {
    if (!orderId || !order) return;
    if (TERMINAL_STATUSES.includes(order.status)) return;

    channelRef.current = supabase
      .channel(`order:${orderId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'orders',
          filter: `id=eq.${orderId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['order', orderId] });
        },
      )
      .subscribe();

    return () => {
      channelRef.current?.unsubscribe();
    };
  }, [orderId, order?.status, queryClient]);

  if (isLoading) {
    return (
      <div className="flex min-h-dvh flex-col" style={{ background: 'var(--bg)' }}>
        <OrderPageHeader onBack={() => navigate(-1)} />
        <div className="flex flex-1 items-center justify-center p-6">
          <div
            className="h-16 w-16 animate-spin rounded-full"
            style={{
              border: '4px solid var(--border)',
              borderTopColor: 'var(--accent)',
            }}
            role="status"
            aria-label="Загрузка заказа"
          />
        </div>
      </div>
    );
  }

  if (isError || !order) {
    return (
      <div className="flex min-h-dvh flex-col" style={{ background: 'var(--bg)' }}>
        <OrderPageHeader onBack={() => navigate(-1)} />
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
          <p className="text-lg font-semibold" style={{ color: 'var(--text)' }}>
            Заказ не найден
          </p>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Проверьте ссылку или попробуйте открыть заказ из истории
          </p>
        </div>
      </div>
    );
  }

  const isTerminal = TERMINAL_STATUSES.includes(order.status);
  const currency = 'RUB';

  return (
    <div
      className="flex min-h-dvh flex-col"
      style={{ background: 'var(--bg)', paddingBottom: 24 }}
    >
      <OrderPageHeader onBack={() => navigate(-1)} />

      <main className="flex flex-col gap-4 px-4 pt-4">
        {/* Status hero */}
        <section
          className="flex flex-col items-center gap-3 rounded-2xl py-8 px-4 text-center"
          style={{ background: 'var(--surface)', boxShadow: 'var(--shadow)' }}
          aria-live="polite"
          aria-atomic="true"
        >
          <span className="text-5xl" role="img" aria-label={ORDER_STATUS_LABEL[order.status]}>
            {STATUS_ICON[order.status]}
          </span>
          <div>
            <p
              className="text-2xl font-bold"
              style={{ color: STATUS_COLOR[order.status] }}
            >
              {ORDER_STATUS_LABEL[order.status]}
            </p>
            <p className="mt-1 text-sm" style={{ color: 'var(--text-muted)' }}>
              Заказ #{order.order_number}
            </p>
          </div>

          {!isTerminal && (
            <p className="text-xs" style={{ color: 'var(--text-subtle)' }}>
              Статус обновляется автоматически
            </p>
          )}

          {order.cancel_reason && (
            <p
              className="rounded-lg px-3 py-2 text-sm"
              style={{ background: 'oklch(55% 0.200 25 / 0.1)', color: 'var(--danger)' }}
            >
              {order.cancel_reason}
            </p>
          )}
        </section>

        {/* Slot and fulfillment */}
        <InfoSection title="Получение">
          <InfoRow label="Способ" value={FULFILLMENT_LABEL[order.fulfillment]} />
          <InfoRow
            label="Время"
            value={formatSlotRange(order.slot_start_at, order.slot_end_at, order.location.timezone)}
          />
          <InfoRow label="Адрес" value={order.location.address} />
          {order.delivery_address && (
            <InfoRow label="Доставить" value={order.delivery_address} />
          )}
        </InfoSection>

        {/* Items */}
        <InfoSection title="Состав заказа">
          <ul className="flex flex-col gap-2">
            {order.items.map((item, idx) => (
              <li key={idx} className="flex justify-between gap-3 text-sm">
                <div className="min-w-0">
                  <p style={{ color: 'var(--text)' }}>
                    {item.name} × {item.quantity}
                  </p>
                  {item.modifiers.length > 0 && (
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      {item.modifiers.map((m) => m.name).join(', ')}
                    </p>
                  )}
                  {item.note && (
                    <p className="text-xs italic" style={{ color: 'var(--text-subtle)' }}>
                      {item.note}
                    </p>
                  )}
                </div>
                <span className="flex-shrink-0 tabular-nums" style={{ color: 'var(--text-muted)' }}>
                  {formatMoney(item.line_total, currency)}
                </span>
              </li>
            ))}
          </ul>

          <div
            className="mt-3 flex flex-col gap-1 border-t pt-3"
            style={{ borderColor: 'var(--border)' }}
          >
            {order.discount_total > 0 && (
              <div className="flex justify-between text-sm">
                <span style={{ color: 'var(--text-muted)' }}>Скидка</span>
                <span className="tabular-nums" style={{ color: 'var(--success)' }}>
                  −{formatMoney(order.discount_total, currency)}
                </span>
              </div>
            )}
            {order.delivery_fee > 0 && (
              <div className="flex justify-between text-sm">
                <span style={{ color: 'var(--text-muted)' }}>Доставка</span>
                <span className="tabular-nums" style={{ color: 'var(--text)' }}>
                  {formatMoney(order.delivery_fee, currency)}
                </span>
              </div>
            )}
            <div className="flex justify-between text-base font-bold">
              <span style={{ color: 'var(--text)' }}>Итого</span>
              <span className="tabular-nums" style={{ color: 'var(--text)' }}>
                {formatMoney(order.total, currency)}
              </span>
            </div>
          </div>
        </InfoSection>

        {/* Payment */}
        <InfoSection title="Оплата">
          <InfoRow label="Способ" value={PAYMENT_LABEL[order.payment_method]} />
          <InfoRow
            label="Статус"
            value={
              order.payment_status === 'paid'
                ? 'Оплачено'
                : order.payment_status === 'pending'
                  ? 'Ожидает оплаты'
                  : order.payment_status === 'failed'
                    ? 'Ошибка оплаты'
                    : order.payment_status
            }
          />
        </InfoSection>

        {/* Contact */}
        {order.location.phone && (
          <InfoSection title="Контакты точки">
            <a
              href={phoneHref(order.location.phone)}
              className="text-sm font-medium"
              style={{ color: 'var(--accent)' }}
            >
              {order.location.phone}
            </a>
          </InfoSection>
        )}

        {/* Cancel */}
        {order.can_cancel && (
          <CancelButton orderId={order.order_id} token={token} />
        )}
      </main>
    </div>
  );
}

function CancelButton({ orderId, token }: { orderId: string; token: string }) {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleCancel() {
    if (!confirm('Отменить заказ?')) return;
    setLoading(true);
    setError(null);
    const { error: err } = await supabase.rpc('cancel_order_by_token', {
      p_order_id: orderId,
      p_token: token,
      p_reason: 'Отменён клиентом',
    });
    setLoading(false);
    if (err) {
      setError(err.message);
    } else {
      queryClient.invalidateQueries({ queryKey: ['order', orderId] });
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {error && (
        <p className="text-sm" style={{ color: 'var(--danger)' }} role="alert">
          {error}
        </p>
      )}
      <button
        onClick={handleCancel}
        disabled={loading}
        className="h-11 w-full rounded-xl text-sm font-medium disabled:opacity-60"
        style={{
          border: '1px solid var(--danger)',
          color: 'var(--danger)',
          background: 'transparent',
        }}
      >
        {loading ? 'Отменяем…' : 'Отменить заказ'}
      </button>
    </div>
  );
}

function InfoSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section
      className="rounded-2xl p-4"
      style={{ background: 'var(--surface)', boxShadow: 'var(--shadow)' }}
    >
      <h2 className="mb-3 text-sm font-semibold" style={{ color: 'var(--text)' }}>
        {title}
      </h2>
      {children}
    </section>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3 py-1 text-sm">
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span className="text-right" style={{ color: 'var(--text)' }}>{value}</span>
    </div>
  );
}

function OrderPageHeader({ onBack }: { onBack: () => void }) {
  return (
    <header
      className="sticky top-0 z-20 flex items-center gap-3 px-2 py-3"
      style={{ background: 'var(--surface)', boxShadow: 'var(--shadow-sm)' }}
    >
      <button
        onClick={onBack}
        aria-label="Назад"
        className="hit-target rounded-lg"
        style={{ color: 'var(--text)' }}
      >
        ←
      </button>
      <h1 className="text-base font-semibold" style={{ color: 'var(--text)' }}>
        Статус заказа
      </h1>
    </header>
  );
}


